import { NavigableModal } from '$components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EditMedicalNotesForm, EditMedicalNotesSchema, Input } from '$features/pets/forms/edit-medical-notes/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { petShowOptions, petShowQueryKey, petUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

export function EditMedicalNotesModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const { mutate: updateMedicalNotes, isPending: isUpdatingMedicalNotes } = useMutation({
    ...petUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: petShowQueryKey({
          path: {
            pet: Number(id),
          },
        }),
      })

      ToastHelper.success('Medical notes updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update medical notes.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: EditMedicalNotesSchema) => {
    updateMedicalNotes({
      path: {
        pet: Number(id),
      },
      body: {
        allergies: values[Input.Allergies],
        medical_notes: values[Input.MedicalNotes],
      },
    })
  }

  const pet = data?.data

  if (!pet) {
    return null
  }

  return (
    <EditMedicalNotesForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingMedicalNotes}
      defaultValues={{
        [Input.Allergies]: pet.allergies ?? undefined,
        [Input.MedicalNotes]: pet.medical_notes ?? undefined,
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Medical Notes
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <EditMedicalNotesForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <EditMedicalNotesForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </EditMedicalNotesForm>
  )
}
